import {
  Archive,
  ArrowLeft,
  Copy,
  Download,
  FileSpreadsheet,
  ListPlus,
  Monitor,
  PanelsTopLeft,
  Plus,
  Rows3,
  Save,
  Settings2,
  Trash2,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { useApi } from "../App.jsx";
import {
  ConfirmDialog,
  FeedbackBanner,
  LoadingBlock,
  PageHeader,
  useRouter,
} from "@tribunalea/ui";

let fieldSequence = 0;

function createUiId(prefix = "field") {
  fieldSequence += 1;
  return globalThis.crypto?.randomUUID?.() || `${prefix}-${fieldSequence}`;
}

function emptyColumn(index = 1) {
  return {
    uiId: createUiId("column"),
    key: `columna_${index}`,
    label: "",
    instructions: "",
    type: "text",
    externalLookup: null,
  };
}

function emptyBlockAttribute(index = 1) {
  return {
    uiId: createUiId("attribute"),
    key: `dato_${index}`,
    label: "",
    instructions: "",
    type: "text",
    required: false,
    externalLookup: null,
    rowOffset: index - 1,
    columnOffset: 0,
  };
}

function emptyBlockInstance(index = 1) {
  return {
    uiId: createUiId("instance"),
    key: `instancia_${index}`,
    label: "",
    instructions: "",
    target: { sheet: "Hoja1", cell: "" },
  };
}

function emptyField(index = 1, kind = "field") {
  return {
    uiId: createUiId(),
    kind,
    key: kind === "range"
      ? `rango_${index}`
      : kind === "block"
        ? `bloque_${index}`
        : `campo_${index}`,
    label: "",
    instructions: "",
    type: "text",
    sourceMode: "extracted",
    defaultValue: null,
    computation: null,
    externalLookup: null,
    required: false,
    target: { sheet: "Hoja1", cell: "", range: "" },
    range: kind === "range" ? { maxRows: 5 } : null,
    columns: kind === "range" ? [emptyColumn()] : [],
    block: kind === "block"
      ? {
          attributes: [emptyBlockAttribute()],
          instances: [emptyBlockInstance()],
        }
      : null,
  };
}

function normalizeKey(value) {
  return value
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("es-AR")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^[^a-z]+|_+$/g, "")
    .slice(0, 64);
}

function columnNumber(name) {
  return [...name].reduce(
    (value, character) => value * 26 + character.charCodeAt(0) - 64,
    0,
  );
}

function columnName(number) {
  let remaining = number;
  let result = "";
  while (remaining > 0) {
    const offset = (remaining - 1) % 26;
    result = String.fromCharCode(65 + offset) + result;
    remaining = Math.floor((remaining - 1) / 26);
  }
  return result;
}

function rangeAddress(startCell, rows, columns) {
  const match = /^([A-Z]{1,3})([1-9]\d*)$/.exec(
    String(startCell || "").trim().toUpperCase(),
  );
  if (!match || !rows || !columns) return "";
  const startColumn = columnNumber(match[1]);
  const startRow = Number(match[2]);
  const end = `${columnName(startColumn + columns - 1)}${startRow + rows - 1}`;
  return `${match[1]}${startRow}:${end}`;
}

function offsetCellAddress(startCell, rowOffset, columnOffset) {
  const match = /^([A-Z]{1,3})([1-9]\d*)$/.exec(
    String(startCell || "").trim().toUpperCase(),
  );
  if (!match) return "";
  return `${columnName(columnNumber(match[1]) + Number(columnOffset || 0))}${
    Number(match[2]) + Number(rowOffset || 0)
  }`;
}

function normalizeField(field) {
  const kind = field.kind || "field";
  const targetCell = field.target?.cell
    || field.target?.range?.split(":")[0]
    || "";
  return {
    ...field,
    uiId: createUiId(),
    kind,
    externalLookup: field.externalLookup || null,
    computation: field.sourceMode === "computed" ? field.computation : null,
    target: {
      sheet: field.target?.sheet || "Hoja1",
      cell: targetCell,
      range: field.target?.range || "",
    },
    range: kind === "range"
      ? { maxRows: Number(field.range?.maxRows || 5) }
      : null,
    columns: kind === "range"
      ? (field.columns || []).map((column) => ({
          ...column,
          uiId: createUiId("column"),
          externalLookup: column.externalLookup || null,
        }))
      : [],
    block: kind === "block"
      ? {
          attributes: (field.block?.attributes || []).map((attribute) => ({
            ...attribute,
            uiId: createUiId("attribute"),
            externalLookup: attribute.externalLookup || null,
            required: Boolean(attribute.required),
            rowOffset: Number(attribute.rowOffset || 0),
            columnOffset: Number(attribute.columnOffset || 0),
          })),
          instances: (field.block?.instances || []).map((instance) => ({
            ...instance,
            uiId: createUiId("instance"),
            target: {
              sheet: instance.target?.sheet || "Hoja1",
              cell: instance.target?.cell || "",
            },
          })),
        }
      : null,
  };
}

function serializedField(field, outputKind) {
  const { uiId: _uiId, columns, block, ...value } = field;
  const isRange = field.kind === "range";
  const isBlock = field.kind === "block";
  return {
    ...value,
    computation: !isRange && !isBlock && field.sourceMode === "computed"
      ? field.computation
      : null,
    defaultValue: !isRange && !isBlock ? field.defaultValue : null,
    target: outputKind === "excel" && !isBlock
      ? {
          sheet: field.target.sheet,
          cell: field.target.cell,
          ...(isRange
            ? {
                range: rangeAddress(
                  field.target.cell,
                  Number(field.range?.maxRows || 1),
                  field.columns.length,
                ),
              }
            : {}),
        }
      : null,
    columns: isRange
      ? columns.map(({ uiId: _columnUiId, ...column }) => column)
      : [],
    block: isBlock
      ? {
          attributes: block.attributes.map(({
            uiId: _attributeUiId,
            ...attribute
          }) => attribute),
          instances: block.instances.map(({
            uiId: _instanceUiId,
            target,
            ...instance
          }) => ({
            ...instance,
            target: outputKind === "excel" ? target : null,
          })),
        }
      : null,
  };
}

function externalLookupIsValid(lookup, options, ownKey) {
  return !lookup || (
    lookup.queryKey !== ownKey
    && options.some(({ key }) => key === lookup.queryKey)
  );
}

function fieldIsValid(field, outputKind, fields) {
  const validTarget = field.kind === "block" || outputKind === "display" || (
    field.target.sheet.trim()
    && /^[A-Z]{1,3}[1-9]\d*$/.test(field.target.cell)
  );
  if (!field.key || !field.label.trim() || !validTarget) return false;
  if (field.kind === "range") {
    const keys = field.columns.map(({ key }) => key);
    return (
      ["extracted", "manual"].includes(field.sourceMode)
      && Number(field.range?.maxRows) > 0
      && field.columns.length > 0
      && field.columns.every((column) => column.key && column.label.trim())
      && field.columns.every((column) => externalLookupIsValid(
        column.externalLookup,
        field.columns,
        column.key,
      ))
      && new Set(keys).size === keys.length
    );
  }
  if (field.kind === "block") {
    const attributeKeys = field.block.attributes.map(({ key }) => key);
    const instanceKeys = field.block.instances.map(({ key }) => key);
    return (
      ["extracted", "manual"].includes(field.sourceMode)
      && field.block.attributes.length > 0
      && field.block.instances.length > 0
      && field.block.attributes.every((attribute) => (
        attribute.key
        && attribute.label.trim()
        && Number(attribute.rowOffset) >= 0
        && Number(attribute.columnOffset) >= 0
      ))
      && field.block.attributes.every((attribute) => externalLookupIsValid(
        attribute.externalLookup,
        field.block.attributes,
        attribute.key,
      ))
      && field.block.instances.every((instance) => (
        instance.key
        && instance.label.trim()
        && (
          outputKind === "display"
          || (
            instance.target.sheet.trim()
            && /^[A-Z]{1,3}[1-9]\d*$/.test(instance.target.cell)
          )
        )
      ))
      && new Set(attributeKeys).size === attributeKeys.length
      && new Set(instanceKeys).size === instanceKeys.length
    );
  }
  return (
    externalLookupIsValid(
      field.externalLookup,
      fields.filter(({ kind }) => kind === "field"),
      field.key,
    )
    && (
      field.sourceMode !== "computed" || (
        field.computation?.sourceKey
        && Number(field.computation?.percentage) > 0
      )
    )
  );
}

function sourceLabel(field) {
  if (field.kind === "range") {
    return field.sourceMode === "manual" ? "Rango manual" : "Rango extraído";
  }
  if (field.kind === "block") {
    return field.sourceMode === "manual" ? "Bloque manual" : "Bloque extraído";
  }
  return {
    extracted: "Extraído",
    manual: "Manual",
    fixed: "Fijo",
    computed: "Calculado",
  }[field.sourceMode] || "Extraído";
}

function typeLabel(type) {
  return {
    text: "Texto",
    number: "Número",
    currency: "Moneda",
    date: "Fecha",
    boolean: "Sí / No",
  }[type] || "Texto";
}

function defaultLookupQuery(options, ownKey) {
  return options.find(({ key, label }) => (
    key !== ownKey
    && /nombre|razon|denominacion|profesional|abogad/i.test(
      `${key} ${label}`,
    )
  ))?.key || options.find(({ key }) => key !== ownKey)?.key || "";
}

function ExternalLookupControls({
  value,
  options,
  ownKey,
  onChange,
  compact = false,
}) {
  const provider = value?.provider || "";
  const queryOptions = options.filter(({ key }) => key !== ownKey);
  return (
    <div className={`external-lookup-controls${compact ? " is-compact" : ""}`}>
      <select
        aria-label="Completar si falta"
        value={provider}
        onChange={(event) => {
          const nextProvider = event.target.value;
          onChange(nextProvider
            ? {
                provider: nextProvider,
                queryKey: value?.queryKey
                  || defaultLookupQuery(queryOptions, ownKey),
              }
            : null);
        }}
      >
        <option value="">Sin enriquecimiento externo</option>
        <option value="tax_id_by_legal_name">Buscar CUIT</option>
        <option value="professional_registration_by_name">
          Buscar matrícula profesional
        </option>
      </select>
      {provider && (
        <select
          aria-label="Dato usado para buscar"
          value={value?.queryKey || ""}
          onChange={(event) => onChange({
            provider,
            queryKey: event.target.value,
          })}
        >
          <option value="">Buscar usando…</option>
          {queryOptions.map((option) => (
            <option key={option.key} value={option.key}>
              {option.label || option.key}
            </option>
          ))}
        </select>
      )}
    </div>
  );
}

function FieldSettings({
  field,
  index,
  fields,
  outputKind,
  patchField,
  patchColumn,
  addColumn,
  removeColumn,
  patchBlockAttribute,
  addBlockAttribute,
  removeBlockAttribute,
  patchBlockInstance,
  addBlockInstance,
  removeBlockInstance,
  duplicateField,
  removeField,
}) {
  const isRange = field.kind === "range";
  const isBlock = field.kind === "block";
  const isStructured = isRange || isBlock;
  const numericSources = fields.filter((candidate) => (
    candidate.kind === "field"
    && candidate.key !== field.key
    && ["number", "currency"].includes(candidate.type)
  ));
  const destinationRange = isRange
    ? rangeAddress(
        field.target.cell,
        Number(field.range?.maxRows || 1),
        field.columns.length,
      )
    : "";

  return (
    <div className="field-settings-panel">
      <div className="field-grid compact-field-grid">
        <label>
          Nombre visible
          <input
            value={field.label}
            onChange={(event) => {
              const label = event.target.value;
              patchField(index, {
                label,
                ...(/^(?:campo|rango|bloque)_\d+$/.test(field.key)
                  ? { key: normalizeKey(label) || field.key }
                  : {}),
              });
            }}
            placeholder={isRange
              ? "Ej. Representantes"
              : isBlock
                ? "Ej. Demandados en costas"
                : "Ej. Fecha del acta"}
          />
        </label>
        <label>
          Identificador
          <input
            value={field.key}
            onChange={(event) => patchField(index, {
              key: normalizeKey(event.target.value),
            })}
          />
        </label>
        <label>
          Origen
          <select
            value={field.sourceMode}
            onChange={(event) => {
              const sourceMode = event.target.value;
              patchField(index, {
                sourceMode,
                computation: sourceMode === "computed"
                  ? {
                      operation: "percentage",
                      sourceKey: numericSources[0]?.key || "",
                      percentage: 2,
                      precision: 2,
                    }
                  : null,
                ...(sourceMode === "extracted"
                  ? {}
                  : {
                      externalLookup: null,
                      columns: field.columns.map((column) => ({
                        ...column,
                        externalLookup: null,
                      })),
                      block: field.block
                        ? {
                            ...field.block,
                            attributes: field.block.attributes.map(
                              (attribute) => ({
                                ...attribute,
                                externalLookup: null,
                              }),
                            ),
                          }
                        : null,
                    }),
              });
            }}
          >
            <option value="extracted">Extraer del documento</option>
            <option value="manual">Completar manualmente</option>
            {!isStructured && <option value="fixed">Valor fijo</option>}
            {!isStructured && <option value="computed">Calcular desde otro campo</option>}
          </select>
        </label>
        {!isStructured && (
          <label>
            Tipo
            <select
              value={field.type}
              onChange={(event) => patchField(index, {
                type: event.target.value,
              })}
            >
              <option value="text">Texto</option>
              <option value="number">Número</option>
              <option value="currency">Moneda</option>
              <option value="date">Fecha</option>
              <option value="boolean">Sí / No</option>
            </select>
          </label>
        )}
        {isRange && (
          <label>
            Máximo de filas
            <input
              type="number"
              min="1"
              max="50"
              value={field.range?.maxRows || 5}
              onChange={(event) => patchField(index, {
                range: { maxRows: Number(event.target.value) },
              })}
            />
          </label>
        )}
        {outputKind === "excel" && !isBlock && (
          <>
            <label>
              Hoja
              <input
                value={field.target.sheet}
                onChange={(event) => patchField(index, {
                  target: { ...field.target, sheet: event.target.value },
                })}
              />
            </label>
            <label>
              {isRange ? "Primera celda" : "Celda"}
              <input
                value={field.target.cell}
                onChange={(event) => patchField(index, {
                  target: {
                    ...field.target,
                    cell: event.target.value.toUpperCase(),
                  },
                })}
                placeholder={isRange ? "B13" : "B13"}
              />
              {isRange && destinationRange && (
                <small className="input-help">
                  Se completará automáticamente {destinationRange}.
                </small>
              )}
            </label>
          </>
        )}
      </div>

      {field.sourceMode === "fixed" && (
        <label className="field-settings-wide">
          Valor fijo
          <input
            value={field.defaultValue ?? ""}
            onChange={(event) => patchField(index, {
              defaultValue: event.target.value,
            })}
          />
        </label>
      )}

      {field.sourceMode === "computed" && (
        <div className="field-grid compact-field-grid">
          <label>
            Campo de origen
            <select
              value={field.computation?.sourceKey || ""}
              onChange={(event) => patchField(index, {
                computation: {
                  operation: "percentage",
                  sourceKey: event.target.value,
                  percentage: Number(field.computation?.percentage ?? 2),
                  precision: Number(field.computation?.precision ?? 2),
                },
              })}
            >
              <option value="">Seleccionar campo</option>
              {numericSources.map((candidate) => (
                <option key={candidate.uiId} value={candidate.key}>
                  {candidate.label || candidate.key}
                </option>
              ))}
            </select>
          </label>
          <label>
            Porcentaje
            <input
              type="number"
              min="0.01"
              max="1000"
              step="0.01"
              value={field.computation?.percentage ?? 2}
              onChange={(event) => patchField(index, {
                computation: {
                  operation: "percentage",
                  sourceKey: field.computation?.sourceKey || "",
                  percentage: Number(event.target.value),
                  precision: Number(field.computation?.precision ?? 2),
                },
              })}
            />
          </label>
        </div>
      )}

      {!isStructured && field.sourceMode === "extracted" && (
        <div className="field-settings-wide external-lookup-section">
          <div>
            <strong>Completar automáticamente si falta</strong>
            <small>
              Consulta una herramienta externa usando otro campo de esta plantilla.
            </small>
          </div>
          <ExternalLookupControls
            value={field.externalLookup}
            options={fields.filter(({ kind }) => kind === "field")}
            ownKey={field.key}
            onChange={(externalLookup) => patchField(index, {
              externalLookup,
            })}
          />
        </div>
      )}

      {isRange && (
        <div className="range-columns-editor">
          <div className="range-columns-heading">
            <div>
              <strong>Columnas del rango</strong>
              <small>Se repiten automáticamente en cada fila.</small>
            </div>
            <button
              type="button"
              className="secondary-button compact-button"
              onClick={() => addColumn(index)}
            >
              <Plus size={15} /> Columna
            </button>
          </div>
          <div className="range-columns-table">
            <div className="range-column-header">
              <span>Nombre</span><span>Tipo</span><span>Cómo encontrarlo</span><span />
            </div>
            {field.columns.map((column, columnIndex) => (
              <div className="range-column-row" key={column.uiId}>
                <div>
                  <input
                    aria-label="Nombre de columna"
                    value={column.label}
                    placeholder="Ej. Nombre"
                    onChange={(event) => {
                      const label = event.target.value;
                      patchColumn(index, columnIndex, {
                        label,
                        ...(/^columna_\d+$/.test(column.key)
                          ? { key: normalizeKey(label) || column.key }
                          : {}),
                      });
                    }}
                  />
                  <input
                    className="range-column-key"
                    aria-label="Identificador de columna"
                    value={column.key}
                    onChange={(event) => patchColumn(index, columnIndex, {
                      key: normalizeKey(event.target.value),
                    })}
                  />
                </div>
                <select
                  aria-label="Tipo de columna"
                  value={column.type}
                  onChange={(event) => patchColumn(index, columnIndex, {
                    type: event.target.value,
                  })}
                >
                  <option value="text">Texto</option>
                  <option value="number">Número</option>
                  <option value="currency">Moneda</option>
                  <option value="date">Fecha</option>
                  <option value="boolean">Sí / No</option>
                </select>
                <div className="lookup-instructions-cell">
                  <input
                    aria-label="Instrucciones de columna"
                    value={column.instructions || ""}
                    placeholder="Instrucción específica de esta columna"
                    onChange={(event) => patchColumn(index, columnIndex, {
                      instructions: event.target.value,
                    })}
                  />
                  {field.sourceMode === "extracted" && (
                    <ExternalLookupControls
                      compact
                      value={column.externalLookup}
                      options={field.columns}
                      ownKey={column.key}
                      onChange={(externalLookup) => patchColumn(
                        index,
                        columnIndex,
                        { externalLookup },
                      )}
                    />
                  )}
                </div>
                <button
                  type="button"
                  className="icon-button danger"
                  disabled={field.columns.length === 1}
                  onClick={() => removeColumn(index, columnIndex)}
                  aria-label="Eliminar columna"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {isBlock && (
        <div className="block-editor-stack">
          <div className="range-columns-editor block-attributes-editor">
            <div className="range-columns-heading">
              <div>
                <strong>Datos del bloque</strong>
                <small>Definilos una sola vez y ubicá cada uno respecto del ancla.</small>
              </div>
              <button
                type="button"
                className="secondary-button compact-button"
                onClick={() => addBlockAttribute(index)}
              >
                <Plus size={15} /> Dato
              </button>
            </div>
            <div className="block-attributes-table">
              <div className="block-attribute-header">
                <span>Dato</span><span>Tipo</span><span>Posición</span>
                <span>Cómo encontrarlo</span><span>Obligatorio</span><span />
              </div>
              {field.block.attributes.map((attribute, attributeIndex) => (
                <div className="block-attribute-row" key={attribute.uiId}>
                  <div>
                    <input
                      aria-label="Nombre del dato"
                      value={attribute.label}
                      placeholder="Ej. CUIT"
                      onChange={(event) => {
                        const label = event.target.value;
                        patchBlockAttribute(index, attributeIndex, {
                          label,
                          ...(/^dato_\d+$/.test(attribute.key)
                            ? { key: normalizeKey(label) || attribute.key }
                            : {}),
                        });
                      }}
                    />
                    <input
                      className="range-column-key"
                      aria-label="Identificador del dato"
                      value={attribute.key}
                      onChange={(event) => patchBlockAttribute(
                        index,
                        attributeIndex,
                        { key: normalizeKey(event.target.value) },
                      )}
                    />
                  </div>
                  <select
                    aria-label="Tipo del dato"
                    value={attribute.type}
                    onChange={(event) => patchBlockAttribute(
                      index,
                      attributeIndex,
                      { type: event.target.value },
                    )}
                  >
                    <option value="text">Texto</option>
                    <option value="number">Número</option>
                    <option value="currency">Moneda</option>
                    <option value="date">Fecha</option>
                    <option value="boolean">Sí / No</option>
                  </select>
                  <div className="block-offset-inputs">
                    <label>
                      Fila +
                      <input
                        type="number"
                        min="0"
                        max="1000"
                        value={attribute.rowOffset}
                        onChange={(event) => patchBlockAttribute(
                          index,
                          attributeIndex,
                          { rowOffset: Number(event.target.value) },
                        )}
                      />
                    </label>
                    <label>
                      Col. +
                      <input
                        type="number"
                        min="0"
                        max="1000"
                        value={attribute.columnOffset}
                        onChange={(event) => patchBlockAttribute(
                          index,
                          attributeIndex,
                          { columnOffset: Number(event.target.value) },
                        )}
                      />
                    </label>
                  </div>
                  <div className="lookup-instructions-cell">
                    <input
                      aria-label="Instrucciones del dato"
                      value={attribute.instructions || ""}
                      placeholder="Instrucción compartida por todas las instancias"
                      onChange={(event) => patchBlockAttribute(
                        index,
                        attributeIndex,
                        { instructions: event.target.value },
                      )}
                    />
                    {field.sourceMode === "extracted" && (
                      <ExternalLookupControls
                        compact
                        value={attribute.externalLookup}
                        options={field.block.attributes}
                        ownKey={attribute.key}
                        onChange={(externalLookup) => patchBlockAttribute(
                          index,
                          attributeIndex,
                          { externalLookup },
                        )}
                      />
                    )}
                  </div>
                  <label className="block-required-toggle">
                    <input
                      type="checkbox"
                      checked={attribute.required}
                      onChange={(event) => patchBlockAttribute(
                        index,
                        attributeIndex,
                        { required: event.target.checked },
                      )}
                    />
                    <span>Sí</span>
                  </label>
                  <button
                    type="button"
                    className="icon-button danger"
                    disabled={field.block.attributes.length === 1}
                    onClick={() => removeBlockAttribute(index, attributeIndex)}
                    aria-label="Eliminar dato"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>

          <div className="range-columns-editor block-instances-editor">
            <div className="range-columns-heading">
              <div>
                <strong>Instancias del bloque</strong>
                <small>Cada ancla reutiliza todos los datos y sus posiciones relativas.</small>
              </div>
              <button
                type="button"
                className="secondary-button compact-button"
                onClick={() => addBlockInstance(index)}
              >
                <Plus size={15} /> Instancia
              </button>
            </div>
            <div className="block-instances-table">
              <div className={`block-instance-header${
                outputKind === "display" ? " is-display" : ""
              }`}>
                <span>Instancia</span><span>Contexto para la IA</span>
                {outputKind === "excel" && <><span>Hoja</span><span>Ancla</span></>}
                <span />
              </div>
              {field.block.instances.map((instance, instanceIndex) => (
                <div
                  className={`block-instance-row${
                    outputKind === "display" ? " is-display" : ""
                  }`}
                  key={instance.uiId}
                >
                  <div>
                    <input
                      aria-label="Nombre de instancia"
                      value={instance.label}
                      placeholder="Ej. Prospera · Demandado 1"
                      onChange={(event) => {
                        const label = event.target.value;
                        patchBlockInstance(index, instanceIndex, {
                          label,
                          ...(/^instancia_\d+$/.test(instance.key)
                            ? { key: normalizeKey(label) || instance.key }
                            : {}),
                        });
                      }}
                    />
                    <input
                      className="range-column-key"
                      aria-label="Identificador de instancia"
                      value={instance.key}
                      onChange={(event) => patchBlockInstance(
                        index,
                        instanceIndex,
                        { key: normalizeKey(event.target.value) },
                      )}
                    />
                  </div>
                  <input
                    aria-label="Contexto de instancia"
                    value={instance.instructions || ""}
                    placeholder="Qué representa esta aparición del bloque"
                    onChange={(event) => patchBlockInstance(
                      index,
                      instanceIndex,
                      { instructions: event.target.value },
                    )}
                  />
                  {outputKind === "excel" && (
                    <>
                      <input
                        aria-label="Hoja de instancia"
                        value={instance.target.sheet}
                        onChange={(event) => patchBlockInstance(
                          index,
                          instanceIndex,
                          {
                            target: {
                              ...instance.target,
                              sheet: event.target.value,
                            },
                          },
                        )}
                      />
                      <div className="block-anchor-cell">
                        <input
                          aria-label="Celda ancla"
                          value={instance.target.cell}
                          placeholder="B29"
                          onChange={(event) => patchBlockInstance(
                            index,
                            instanceIndex,
                            {
                              target: {
                                ...instance.target,
                                cell: event.target.value.toUpperCase(),
                              },
                            },
                          )}
                        />
                        {instance.target.cell && (
                          <small>
                            {field.block.attributes.length} celdas · {instance.target.cell}
                            {field.block.attributes.length > 1 && ` … ${offsetCellAddress(
                              instance.target.cell,
                              field.block.attributes.at(-1)?.rowOffset,
                              field.block.attributes.at(-1)?.columnOffset,
                            )}`}
                          </small>
                        )}
                      </div>
                    </>
                  )}
                  <button
                    type="button"
                    className="icon-button danger"
                    disabled={field.block.instances.length === 1}
                    onClick={() => removeBlockInstance(index, instanceIndex)}
                    aria-label="Eliminar instancia"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {field.sourceMode !== "computed" && (
        <label className="field-settings-wide">
          {isRange
            ? "Instrucciones para todo el rango"
            : isBlock
              ? "Instrucciones para todo el bloque"
              : "Instrucciones para identificarlo"}
          <textarea
            value={field.instructions || ""}
            onChange={(event) => patchField(index, {
              instructions: event.target.value,
            })}
            placeholder={isRange
              ? "Ej. Una fila por representante, respetando el orden en que aparecen."
              : isBlock
                ? "Ej. Cada instancia representa a un condenado en costas distinto."
                : "Ej. Buscar la fecha del encabezado del acta, no la fecha de presentación."}
          />
        </label>
      )}

      <div className="field-settings-footer">
        {!isBlock && <label className="checkbox-field compact-checkbox">
          <input
            type="checkbox"
            checked={field.required}
            onChange={(event) => patchField(index, {
              required: event.target.checked,
            })}
          />
          <span>
            <strong>{isRange ? "Debe existir al menos una fila" : "Dato obligatorio"}</strong>
            <small>Se señalará durante la revisión si queda vacío.</small>
          </span>
        </label>}
        <div>
          <button
            type="button"
            className="secondary-button compact-button"
            onClick={() => duplicateField(index)}
          >
            <Copy size={15} /> Duplicar
          </button>
          <button
            type="button"
            className="danger-button compact-button"
            disabled={fields.length === 1}
            onClick={() => removeField(index)}
          >
            <Trash2 size={15} /> Eliminar
          </button>
        </div>
      </div>
    </div>
  );
}

export function TemplateEditorPage({ templateId = null }) {
  const api = useApi();
  const { navigate } = useRouter();
  const editing = Boolean(templateId);
  const initialField = emptyField();
  const [form, setForm] = useState({
    name: "",
    description: "",
    generalInstructions: "",
    outputKind: "excel",
    file: null,
    fields: [initialField],
  });
  const [expandedFieldId, setExpandedFieldId] = useState(initialField.uiId);
  const [meta, setMeta] = useState(null);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [state, setState] = useState({
    loading: editing,
    saving: false,
    error: "",
  });

  useEffect(() => {
    if (!templateId) return;
    api.getTemplate(templateId)
      .then((template) => {
        const fields = template.version?.fields?.length
          ? template.version.fields.map(normalizeField)
          : [emptyField()];
        setMeta(template);
        setForm({
          name: template.name,
          description: template.description || "",
          generalInstructions: template.version?.generalInstructions || "",
          outputKind: template.version?.outputKind || "excel",
          file: null,
          fields,
        });
        setExpandedFieldId(fields[0]?.uiId || null);
        setState({ loading: false, saving: false, error: "" });
      })
      .catch((error) => setState({
        loading: false,
        saving: false,
        error: error.message,
      }));
  }, [api, templateId]);

  const valid = useMemo(() => (
    form.name.trim().length >= 2
    && (
      form.outputKind === "display"
      || form.file
      || (editing && meta?.version?.assetId)
    )
    && form.fields.length > 0
    && form.fields.every((field) => fieldIsValid(
      field,
      form.outputKind,
      form.fields,
    ))
    && new Set(form.fields.map(({ key }) => key)).size === form.fields.length
  ), [editing, form, meta]);

  const destinationCount = useMemo(
    () => form.fields.reduce((total, field) => (
      total + (field.kind === "range"
        ? Number(field.range?.maxRows || 0) * field.columns.length
        : field.kind === "block"
          ? field.block.attributes.length * field.block.instances.length
        : 1)
    ), 0),
    [form.fields],
  );

  const patchField = (index, patch) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, candidate) =>
        candidate === index ? { ...field, ...patch } : field),
    }));
  };

  const patchColumn = (fieldIndex, columnIndex, patch) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, candidate) => candidate === fieldIndex
        ? {
            ...field,
            columns: field.columns.map((column, columnCandidate) =>
              columnCandidate === columnIndex
                ? { ...column, ...patch }
                : column),
          }
        : field),
    }));
  };

  const patchBlockAttribute = (fieldIndex, attributeIndex, patch) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, candidate) => candidate === fieldIndex
        ? {
            ...field,
            block: {
              ...field.block,
              attributes: field.block.attributes.map((attribute, item) =>
                item === attributeIndex ? { ...attribute, ...patch } : attribute),
            },
          }
        : field),
    }));
  };

  const patchBlockInstance = (fieldIndex, instanceIndex, patch) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, candidate) => candidate === fieldIndex
        ? {
            ...field,
            block: {
              ...field.block,
              instances: field.block.instances.map((instance, item) =>
                item === instanceIndex ? { ...instance, ...patch } : instance),
            },
          }
        : field),
    }));
  };

  const addField = (kind = "field") => {
    const field = emptyField(form.fields.length + 1, kind);
    setForm((current) => ({
      ...current,
      fields: [...current.fields, field],
    }));
    setExpandedFieldId(field.uiId);
  };

  const duplicateField = (index) => {
    const source = form.fields[index];
    const existingKeys = new Set(form.fields.map(({ key }) => key));
    const baseKey = `${source.key.slice(0, 56)}_copia`;
    let key = baseKey;
    let copyNumber = 2;
    while (existingKeys.has(key)) {
      key = `${baseKey.slice(0, 60)}_${copyNumber}`;
      copyNumber += 1;
    }
    const copy = {
      ...source,
      uiId: createUiId(),
      key,
      label: source.label ? `${source.label} (copia)` : "",
      columns: source.columns.map((column) => ({
        ...column,
        uiId: createUiId("column"),
      })),
      block: source.block
        ? {
            attributes: source.block.attributes.map((attribute) => ({
              ...attribute,
              uiId: createUiId("attribute"),
            })),
            instances: source.block.instances.map((instance) => ({
              ...instance,
              uiId: createUiId("instance"),
              target: { ...instance.target },
            })),
          }
        : null,
    };
    setForm((current) => ({
      ...current,
      fields: [
        ...current.fields.slice(0, index + 1),
        copy,
        ...current.fields.slice(index + 1),
      ],
    }));
    setExpandedFieldId(copy.uiId);
  };

  const addColumn = (index) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, candidate) => candidate === index
        ? {
            ...field,
            columns: [...field.columns, emptyColumn(field.columns.length + 1)],
          }
        : field),
    }));
  };

  const removeColumn = (fieldIndex, columnIndex) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, candidate) => candidate === fieldIndex
        ? {
            ...field,
            columns: field.columns.filter(
              (_column, columnCandidate) => columnCandidate !== columnIndex,
            ),
          }
        : field),
    }));
  };

  const addBlockAttribute = (index) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, candidate) => candidate === index
        ? {
            ...field,
            block: {
              ...field.block,
              attributes: [
                ...field.block.attributes,
                emptyBlockAttribute(field.block.attributes.length + 1),
              ],
            },
          }
        : field),
    }));
  };

  const removeBlockAttribute = (fieldIndex, attributeIndex) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, candidate) => candidate === fieldIndex
        ? {
            ...field,
            block: {
              ...field.block,
              attributes: field.block.attributes.filter(
                (_attribute, item) => item !== attributeIndex,
              ),
            },
          }
        : field),
    }));
  };

  const addBlockInstance = (index) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, candidate) => candidate === index
        ? {
            ...field,
            block: {
              ...field.block,
              instances: [
                ...field.block.instances,
                emptyBlockInstance(field.block.instances.length + 1),
              ],
            },
          }
        : field),
    }));
  };

  const removeBlockInstance = (fieldIndex, instanceIndex) => {
    setForm((current) => ({
      ...current,
      fields: current.fields.map((field, candidate) => candidate === fieldIndex
        ? {
            ...field,
            block: {
              ...field.block,
              instances: field.block.instances.filter(
                (_instance, item) => item !== instanceIndex,
              ),
            },
          }
        : field),
    }));
  };

  const removeField = (index) => {
    const removed = form.fields[index];
    const remainingFields = form.fields.filter(
      (_, candidate) => candidate !== index,
    );
    const nextExpandedId = remainingFields[
      Math.min(index, remainingFields.length - 1)
    ]?.uiId;
    setForm((current) => {
      const remaining = current.fields
        .filter((_, candidate) => candidate !== index)
        .map((field) => (
          field.sourceMode === "computed"
          && field.computation?.sourceKey === removed?.key
            ? {
                ...field,
                computation: { ...field.computation, sourceKey: "" },
              }
            : field
        ));
      return { ...current, fields: remaining };
    });
    if (removed?.uiId === expandedFieldId) {
      setExpandedFieldId(nextExpandedId);
    }
  };

  const save = async (event) => {
    event.preventDefault();
    if (!valid || state.saving) return;
    setState((current) => ({ ...current, saving: true, error: "" }));
    try {
      const body = new FormData();
      body.append("name", form.name);
      body.append("description", form.description);
      body.append("generalInstructions", form.generalInstructions);
      body.append("outputKind", form.outputKind);
      body.append("fields", JSON.stringify(
        form.fields.map((field) => serializedField(field, form.outputKind)),
      ));
      if (form.file) body.append("template", form.file);
      const template = editing
        ? await api.createTemplateVersion(templateId, body)
        : await api.createTemplate(body);
      navigate(`/procesador-documental/plantillas/${template.id}`, {
        replace: true,
      });
      if (editing) {
        setMeta(template);
        setForm((current) => ({ ...current, file: null }));
      }
      setState({ loading: false, saving: false, error: "" });
    } catch (error) {
      setState((current) => ({
        ...current,
        saving: false,
        error: error.message,
      }));
    }
  };

  if (state.loading) {
    return (
      <div className="page">
        <LoadingBlock>Abriendo la plantilla…</LoadingBlock>
      </div>
    );
  }

  return (
    <div className="page template-editor-page">
      <button
        className="back-link"
        onClick={() => navigate("/procesador-documental/plantillas")}
      >
        <ArrowLeft size={16} /> Volver
      </button>
      <PageHeader
        eyebrow={editing
          ? `Plantilla · Versión ${meta?.currentVersion}`
          : "Nueva plantilla"}
        title={editing ? form.name : "Configurá un formato reutilizable"}
        description="Cada cambio publicado crea una versión nueva y conserva las configuraciones anteriores."
        actions={editing ? (
          <div className="header-actions">
            {meta?.version?.outputKind === "excel" && (
              <button
                className="secondary-button"
                onClick={() => api.downloadTemplate(templateId)}
              >
                <Download size={17} /> Descargar original
              </button>
            )}
            <button
              className="danger-button"
              onClick={() => setArchiveOpen(true)}
            >
              <Archive size={17} /> Archivar
            </button>
          </div>
        ) : null}
      />

      {state.error && <FeedbackBanner>{state.error}</FeedbackBanner>}

      <form onSubmit={save} className="editor-form">
        <section className="form-card">
          <div className="form-card-title">
            <span className="step-number">1</span>
            <div>
              <h2>Nombre y tipo de resultado</h2>
              <p>Podés mostrar los datos o completar una planilla.</p>
            </div>
          </div>
          <div className="form-grid">
            <label>
              Nombre
              <input
                data-justiniano-target="template-name"
                value={form.name}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  name: event.target.value,
                }))}
                placeholder="Ej. Ficha de inspección"
              />
            </label>
            <label>
              Descripción
              <input
                value={form.description}
                onChange={(event) => setForm((current) => ({
                  ...current,
                  description: event.target.value,
                }))}
                placeholder="Cuándo debe usarse esta plantilla"
              />
            </label>
          </div>
          <div className="output-kind-grid">
            <button
              type="button"
              className={`output-kind-card ${form.outputKind === "display" ? "selected" : ""}`}
              onClick={() => setForm((current) => ({
                ...current,
                outputKind: "display",
                file: null,
              }))}
            >
              <Monitor size={22} />
              <span><strong>Resultado en pantalla</strong><small>Extraer, revisar y consultar los datos.</small></span>
            </button>
            <button
              type="button"
              className={`output-kind-card ${form.outputKind === "excel" ? "selected" : ""}`}
              onClick={() => setForm((current) => ({
                ...current,
                outputKind: "excel",
              }))}
            >
              <FileSpreadsheet size={22} />
              <span><strong>Completar Excel</strong><small>Escribir valores en celdas específicas.</small></span>
            </button>
          </div>
          {form.outputKind === "excel" && (
            <label className="file-drop compact">
              <FileSpreadsheet size={24} />
              <span>
                <strong>
                  {form.file?.name || (
                    editing && meta?.version?.assetId
                      ? "Reemplazar archivo (opcional)"
                      : "Seleccionar Excel vacío"
                  )}
                </strong>
                <small>.xlsx · máximo 5 MB · se conserva sin corregir</small>
              </span>
              <input
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                onChange={(event) => setForm((current) => ({
                  ...current,
                  file: event.target.files?.[0] || null,
                }))}
              />
            </label>
          )}
        </section>

        <section className="form-card general-instructions-card">
          <div className="form-card-title">
            <span className="step-number">2</span>
            <div>
              <h2>Instrucciones generales para la IA</h2>
              <p>Explicá una vez las reglas que se aplican a toda esta plantilla.</p>
            </div>
          </div>
          <label>
            Cómo interpretar estos documentos
            <textarea
              value={form.generalInstructions}
              onChange={(event) => setForm((current) => ({
                ...current,
                generalInstructions: event.target.value,
              }))}
              placeholder="Ej. Los representantes aparecen en el anexo II. Para las fechas, usar la fecha del encabezado del acta y no la fecha de presentación."
            />
            <small className="input-help">
              Cada campo puede agregar después una indicación más específica.
            </small>
          </label>
        </section>

        <section className="form-card fields-card">
          <div className="form-card-title with-action">
            <div className="title-cluster">
              <span className="step-number">3</span>
              <div>
                <h2>Campos {form.outputKind === "excel" ? "y destinos" : ""}</h2>
                <p>Una fila por configuración. Abrí sólo el campo que necesites editar.</p>
              </div>
            </div>
            <div className="field-add-actions">
              <button
                type="button"
                className="secondary-button"
                onClick={() => addField("field")}
              >
                <Plus size={16} /> Campo
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => addField("range")}
              >
                <Rows3 size={16} /> Rango repetitivo
              </button>
              <button
                type="button"
                className="secondary-button"
                onClick={() => addField("block")}
              >
                <PanelsTopLeft size={16} /> Bloque reutilizable
              </button>
            </div>
          </div>

          <div className="template-fields-table">
            <div className="template-fields-header" aria-hidden="true">
              <span>Campo</span>
              <span>Formato</span>
              <span>Destino</span>
              <span>Configuración</span>
            </div>
            {form.fields.map((field, index) => {
              const isRange = field.kind === "range";
              const isBlock = field.kind === "block";
              const destination = form.outputKind === "display"
                ? "En pantalla"
                : isBlock
                  ? `${field.block.instances.length} ubicaciones · ${
                    field.block.instances[0]?.target?.sheet || "Sin hoja"
                  }`
                : field.target.cell
                  ? `${field.target.sheet}!${isRange
                    ? rangeAddress(
                        field.target.cell,
                        Number(field.range?.maxRows || 1),
                        field.columns.length,
                      )
                    : field.target.cell}`
                  : "Sin destino";
              const detail = isRange
                ? `${field.range?.maxRows || 0} filas × ${field.columns.length} columnas`
                : isBlock
                  ? `${field.block.instances.length} instancias × ${field.block.attributes.length} datos`
                : field.instructions || field.key;
              const expanded = expandedFieldId === field.uiId;
              return (
                <div
                  className={`template-field-entry${expanded ? " is-expanded" : ""}`}
                  key={field.uiId}
                >
                  <div className="template-field-row">
                    <span className="template-field-index">
                      {isRange
                        ? <Rows3 size={16} />
                        : isBlock
                          ? <PanelsTopLeft size={16} />
                          : index + 1}
                    </span>
                    <div className="template-field-name">
                      <strong>{field.label || (isRange
                        ? "Rango sin nombre"
                        : isBlock
                          ? "Bloque sin nombre"
                          : "Campo sin nombre")}</strong>
                      <small>{detail}</small>
                    </div>
                    <div className="template-field-format">
                      <strong>{sourceLabel(field)}</strong>
                      <small>{isRange
                        ? "Tabla"
                        : isBlock
                          ? "Formulario repetido"
                          : typeLabel(field.type)}</small>
                    </div>
                    <div className="template-field-destination">
                      <strong>{destination}</strong>
                      <small>{field.required ? "Obligatorio" : "Opcional"}</small>
                    </div>
                    <button
                      type="button"
                      className={`field-config-button${expanded ? " is-active" : ""}`}
                      onClick={() => setExpandedFieldId(
                        expanded ? null : field.uiId,
                      )}
                      aria-expanded={expanded}
                    >
                      <Settings2 size={16} />
                      {expanded ? "Cerrar" : "Configurar"}
                    </button>
                  </div>
                  {expanded && (
                    <FieldSettings
                      field={field}
                      index={index}
                      fields={form.fields}
                      outputKind={form.outputKind}
                      patchField={patchField}
                      patchColumn={patchColumn}
                      addColumn={addColumn}
                      removeColumn={removeColumn}
                      patchBlockAttribute={patchBlockAttribute}
                      addBlockAttribute={addBlockAttribute}
                      removeBlockAttribute={removeBlockAttribute}
                      patchBlockInstance={patchBlockInstance}
                      addBlockInstance={addBlockInstance}
                      removeBlockInstance={removeBlockInstance}
                      duplicateField={duplicateField}
                      removeField={removeField}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="field-list-actions compact-list-actions">
            <button
              type="button"
              className="secondary-button"
              onClick={() => addField("field")}
            >
              <ListPlus size={17} /> Agregar otro campo
            </button>
          </div>
        </section>

        <div className="sticky-actions">
          <span>
            {form.fields.length} configuración{form.fields.length === 1 ? "" : "es"}
            {form.outputKind === "excel" && ` · ${destinationCount} celdas`}
          </span>
          <button className="primary-button" disabled={!valid || state.saving}>
            <Save size={18} />
            {state.saving
              ? "Guardando…"
              : editing
                ? "Publicar nueva versión"
                : "Guardar plantilla"}
          </button>
        </div>
      </form>
      {archiveOpen && (
        <ConfirmDialog
          title="¿Archivar esta plantilla?"
          description="Dejará de aparecer entre las plantillas activas, pero sus versiones y procesamientos anteriores se conservarán."
          confirmLabel="Archivar plantilla"
          busy={state.saving}
          onClose={() => setArchiveOpen(false)}
          onConfirm={async () => {
            setState((current) => ({
              ...current,
              saving: true,
              error: "",
            }));
            try {
              await api.archiveTemplate(templateId);
              navigate("/procesador-documental/plantillas");
            } catch (error) {
              setArchiveOpen(false);
              setState((current) => ({
                ...current,
                saving: false,
                error: error.message,
              }));
            }
          }}
        />
      )}
    </div>
  );
}
